import { useQueryMutation } from '@shared/hooks/useQueryMutation';
import type { PhotoFileType } from '@shared/types/process';

import {
    MAX_PHOTO_FIELD_SIZE_BYTES,
    validatePhotoFile,
} from './processFieldPhotoUtils';

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

type Params = {
    acceptTypes: PhotoFileType[];
    max: number;
    maxSize: number;
    onChange: (value: string[]) => void;
    photoIds: string[];
};

export function useProcessFieldPhotoFiles({
    acceptTypes,
    max,
    maxSize,
    onChange,
    photoIds,
}: Params) {
    const { t } = useTranslation();
    const isUploadingRef = useRef(false);
    const uploadedSizesRef = useRef<Record<string, number>>({});
    const [isUploading, setIsUploading] = useState(false);
    const { mutateAsync: upload } = useQueryMutation<FormData, string>(
        'flow/Files/UploadForm',
        'post',
    );

    const handleFiles = useCallback(
        async (files: File[]) => {
            if (isUploadingRef.current) return;

            isUploadingRef.current = true;
            setIsUploading(true);

            const nextIds = [...photoIds];
            let totalSize = photoIds.reduce(
                (total, id) => total + (uploadedSizesRef.current[id] ?? 0),
                0,
            );

            try {
                for (const [index, file] of files.entries()) {
                    if (nextIds.length >= max) {
                        toast.error(
                            t('translation:photoFileMaxCountError', {
                                file_name: file.name,
                                max_count: max,
                            }),
                        );
                        continue;
                    }

                    const validationError = await validatePhotoFile(
                        file,
                        acceptTypes,
                        maxSize,
                    );

                    if (validationError === 'format') {
                        toast.error(
                            t('translation:photoFileFormatError', {
                                file_name: file.name,
                                formats: acceptTypes
                                    .map((type) => type.toLowerCase())
                                    .join(', '),
                            }),
                        );
                        continue;
                    }

                    if (validationError === 'size') {
                        toast.error(
                            t('translation:photoFileSizeError', {
                                file_name: file.name,
                                max_size: maxSize,
                            }),
                        );
                        continue;
                    }

                    if (totalSize + file.size > MAX_PHOTO_FIELD_SIZE_BYTES) {
                        toast.error(
                            t('translation:photoFileTotalSizeError', {
                                file_names: files
                                    .slice(index)
                                    .map((item) => item.name)
                                    .join(', '),
                            }),
                        );
                        break;
                    }

                    const formData = new FormData();
                    formData.set('File', file);

                    try {
                        const id = await upload(formData);
                        nextIds.push(id);
                        uploadedSizesRef.current[id] = file.size;
                        totalSize += file.size;
                        onChange([...nextIds]);
                    } catch {
                        // The query layer shows the server error. Continue with the next file.
                    }
                }
            } finally {
                isUploadingRef.current = false;
                setIsUploading(false);
            }
        },
        [acceptTypes, max, maxSize, onChange, photoIds, t, upload],
    );

    const removePhoto = useCallback(
        (id: string) => {
            delete uploadedSizesRef.current[id];
            onChange(photoIds.filter((photoId) => photoId !== id));
        },
        [onChange, photoIds],
    );

    return { handleFiles, isUploading, removePhoto };
}
